/**
 * AuditLogPanel — Consultation du journal d'audit global.
 * Admin uniquement. À monter dans une nouvelle tab SADAdmin → Sécurité.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Shield, RefreshCw, Filter, Search, Loader2, AlertTriangle,
  CheckCircle2, Info, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SEVERITY_STYLES = {
  info: { icon: Info, color: 'text-zinc-500', bg: 'bg-zinc-50 border-zinc-200' },
  warning: { icon: AlertTriangle, color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
  critical: { icon: AlertTriangle, color: 'text-red-700', bg: 'bg-red-50 border-red-200' },
};

const ACTION_LABELS = {
  'auth.login': 'Connexion réussie',
  'auth.login_failed': 'Échec connexion',
  'auth.login_blocked': 'Connexion bloquée',
  'auth.logout': 'Déconnexion',
  'auth.password_changed': 'Mot de passe modifié',
  'user.created': 'Utilisateur créé',
  'user.role_changed': 'Rôle modifié',
  'user.password_reset': 'Mot de passe reset',
  'user.deactivated': 'Utilisateur désactivé',
  'user.activated': 'Utilisateur réactivé',
  'user.deleted': 'Utilisateur supprimé',
};

const fmtAt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' }); }
  catch { return iso; }
};

const AuditLogPanel = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ module: '', severity: '', action: '', actor: '' });
  const [page, setPage] = useState(0);
  const [meta, setMeta] = useState({ actions: [], modules: [] });
  const limit = 50;
  const token = localStorage.getItem('dermascan_token');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit), skip: String(page * limit) });
      if (filters.module) params.append('module', filters.module);
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.action) params.append('action', filters.action);
      const r = await axios.get(`${API_URL}/audit/logs?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setLogs(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement audit');
    } finally { setLoading(false); }
  }, [filters, page, token]);

  useEffect(() => {
    axios.get(`${API_URL}/audit/actions`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setMeta(r.data))
      .catch(() => {});
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Filtrage par acteur en frontend (recherche libre nom/IP/cible)
  const filtered = !filters.actor ? logs : logs.filter((l) => {
    const q = filters.actor.toLowerCase();
    return (l.actor_name || '').toLowerCase().includes(q)
      || (l.actor_ip || '').includes(q)
      || (l.target_label || '').toLowerCase().includes(q)
      || (l.target_id || '').toLowerCase().includes(q);
  });

  return (
    <Card className="border-zinc-200" data-testid="audit-log-panel">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Shield className="w-5 h-5 text-violet-600" />
          Journal d'audit
        </CardTitle>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Rafraîchir
        </Button>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-2 flex-wrap p-2 bg-zinc-50 rounded-lg mb-4">
          <div className="flex items-center gap-1 text-xs text-zinc-500">
            <Filter className="w-3.5 h-3.5" /> Filtres
          </div>
          <select className="h-8 px-2 rounded-md border border-zinc-300 text-xs bg-white"
            value={filters.module} onChange={(e) => { setPage(0); setFilters((f) => ({ ...f, module: e.target.value })); }}
            data-testid="audit-filter-module">
            <option value="">Tous modules</option>
            {meta.modules.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select className="h-8 px-2 rounded-md border border-zinc-300 text-xs bg-white"
            value={filters.action} onChange={(e) => { setPage(0); setFilters((f) => ({ ...f, action: e.target.value })); }}
            data-testid="audit-filter-action">
            <option value="">Toutes actions</option>
            {meta.actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select className="h-8 px-2 rounded-md border border-zinc-300 text-xs bg-white"
            value={filters.severity} onChange={(e) => { setPage(0); setFilters((f) => ({ ...f, severity: e.target.value })); }}
            data-testid="audit-filter-severity">
            <option value="">Toutes sévérités</option>
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="critical">Critical</option>
          </select>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              type="text"
              className="h-8 pl-7 text-xs w-56"
              placeholder="Rechercher acteur / IP / cible…"
              value={filters.actor}
              onChange={(e) => setFilters((f) => ({ ...f, actor: e.target.value }))}
              data-testid="audit-filter-actor"
            />
          </div>
          <span className="text-[10px] text-zinc-500 ml-auto">{total} événements · page {page + 1}/{Math.max(1, Math.ceil(total / limit))}</span>
        </div>

        {loading ? (
          <Loader2 className="w-6 h-6 mx-auto animate-spin text-zinc-400" />
        ) : filtered.length === 0 ? (
          <p className="text-center text-zinc-400 italic py-6">Aucun événement.</p>
        ) : (
          <div className="space-y-1.5" data-testid="audit-log-list">
            {filtered.map((log) => {
              const cfg = SEVERITY_STYLES[log.severity] || SEVERITY_STYLES.info;
              const Icon = cfg.icon;
              const label = ACTION_LABELS[log.action] || log.action;
              return (
                <div key={log.id} className={`flex items-start gap-2 px-3 py-2 rounded-md border ${cfg.bg}`} data-testid={`audit-row-${log.id}`}>
                  <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${cfg.color}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="font-semibold">{log.actor_name || 'Anonyme'}</span>
                      {log.actor_role && <span className="text-[10px] text-zinc-500 ml-1">({log.actor_role})</span>}
                      {' · '}
                      <span className="font-medium">{label}</span>
                      {log.target_label && (
                        <span className="text-zinc-600"> → <span className="font-medium">{log.target_label}</span></span>
                      )}
                    </p>
                    <p className="text-[10px] text-zinc-500 flex items-center gap-2 mt-0.5">
                      <span>{fmtAt(log.at)}</span>
                      {log.actor_ip && <span>· IP {log.actor_ip}</span>}
                      <Badge variant="outline" className="text-[9px] py-0 h-4">{log.module}</Badge>
                    </p>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <details className="text-[10px] text-zinc-600 mt-1">
                        <summary className="cursor-pointer hover:text-zinc-900">détails</summary>
                        <pre className="bg-white border border-zinc-200 rounded p-1.5 mt-1 overflow-x-auto">{JSON.stringify(log.details, null, 2)}</pre>
                      </details>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between mt-4">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
          </Button>
          <Button variant="outline" size="sm" disabled={(page + 1) * limit >= total} onClick={() => setPage((p) => p + 1)}>
            Suivant <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AuditLogPanel;
